import pako from 'pako';
import { Base64 } from 'js-base64';

export function encodeFiltersToParams(filters: any[]): URLSearchParams {
  const params = new URLSearchParams();

  filters.forEach(filter => {
    if (!filter.value && filter.value !== 0) return;

    const table = filter.isEvidence ? 'evidence' : 'summary';
    const key = `filters-${table}-${filter.name}-${filter.operator}`;

    if (Array.isArray(filter.value)) {
      params.set(key, filter.value.join(','));
    } else {
      params.set(key, String(filter.value));
    }

    if (filter.dir && filter.dir !== '') {
      params.set(`filters-${table}-${filter.name}-dir`, filter.dir);
    }
  });

  return params;
}

export function decodeFiltersFromParams(searchParams: URLSearchParams): any[] {
  const filters: any[] = [];
  const filterMap = new Map<string, any>();

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('filters-')) {
      const parts = key.split('-');
      if (parts.length >= 4) {
        const table = parts[1];
        const operator = parts[parts.length - 1];
        const fieldName = parts.slice(2, -1).join('-');

        const isEvidence = table === 'evidence';
        const filterKey = `${table}-${fieldName}-${operator}`;

        if (operator === 'dir') {
          const actualFieldName = parts.slice(2).join('-').replace('-dir', '');
          const actualFilterKey = `${table}-${actualFieldName}`;

          for (const [fKey, fValue] of filterMap.entries()) {
            if (fKey.startsWith(actualFilterKey)) {
              fValue.dir = value;
              break;
            }
          }
          continue;
        }

        if (!filterMap.has(filterKey)) {
          filterMap.set(filterKey, {
            name: fieldName,
            isEvidence,
            value: '',
            operator: operator,
            type: 'string',
          });
        }

        const filter = filterMap.get(filterKey);

        filter.value = value.includes(',') ? value.split(',') : value;

        if (['gte', 'lte', 'gt', 'lt'].includes(operator)) {
          filter.type = 'number';
        } else if (['eq'].includes(operator)) {
          filter.type = 'string';
        } else if (['contains', 'startsWith'].includes(operator)) {
          filter.type = 'string';
        }
      }
      continue;
    }

    if (key.startsWith('filters[')) {
      const match = key.match(/^filters\[(\w+)\]\[([^\]]+)\]\[([^\]]+)\]$/);
      if (!match) continue;

      const [, table, fieldName, operatorOrMeta] = match;
      const isEvidence = table === 'evidence';
      const filterKey = `${table}-${fieldName}-${operatorOrMeta}`;

      if (!filterMap.has(filterKey)) {
        filterMap.set(filterKey, {
          name: fieldName,
          isEvidence,
          value: '',
          operator: '',
          type: 'string',
        });
      }

      const filter = filterMap.get(filterKey);

      if (operatorOrMeta.startsWith('_')) {
        if (operatorOrMeta === '_dir') {
          filter.dir = value;
        }
      } else {
        filter.operator = operatorOrMeta;
        filter.value = value.includes(',') ? value.split(',') : value;

        if (['gte', 'lte', 'gt', 'lt'].includes(operatorOrMeta)) {
          filter.type = 'number';
        } else if (['contains', 'startsWith'].includes(operatorOrMeta)) {
          filter.type = 'string';
        }
      }
    }
  }

  const result = Array.from(filterMap.values());
  return result;
}

/**
 * Legacy encode function - kept for backward compatibility
 * Uses compression and base64 encoding
 */
export function encodeData(data: any): string {
  const minimized = data.map(filter => {
    const min: any = {
      n: filter.name,
      o: filter.operator,
      t: filter.type,
      v: filter.value,
    };

    if (filter.integer) min.i = filter.integer;
    if (filter.isEvidence !== undefined) min.e = filter.isEvidence;
    if (filter.dir) min.d = filter.dir;

    return min;
  });

  const json = JSON.stringify(minimized);
  const compressed = pako.deflate(json);
  return Base64.fromUint8Array(compressed, true);
}

/**
 * Legacy decode function - kept for backward compatibility
 * Handles both old compressed format and new URL params format
 */
export function decodeData(token: string): any {
  try {
    if (!token) return [];

    const compressed = Base64.toUint8Array(token);
    const json = pako.inflate(compressed, { to: 'string' });
    const data = JSON.parse(json);

    if (data[0]?.name) {
      return data;
    }

    return data.map(filter => {
      const restored: any = {
        name: filter.n,
        operator: filter.o,
        type: filter.t,
        value: filter.v,
      };

      if (filter.i !== undefined) restored.integer = filter.i;
      if (filter.e !== undefined) restored.isEvidence = filter.e;
      if (filter.d) restored.dir = filter.d;

      return restored;
    });
  } catch (error) {
    console.error('Error decoding filter token:', error);
    return [];
  }
}
